import {
  Anchor,
  Button,
  Card,
  H5,
  Icon,
  InputContainer,
  InputOption,
  InputSelect,
  InputNumber,
} from "@sellar-io/components";
import { Alert } from "../../alert/Alert";
import { DialogNavigation, DialogContent, DialogCTA } from "../../modal/Dialog";
import { useFullPageDialogContext } from "../../modal/useFullPageDialogContext";
import { CourierHaulierSwitchPromptPages } from "../CourierHaulierSwitchPrompt.shared";
import { AlertCompact } from "../../alert/AlertCompact";

import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { BadgeGreen, BadgeRed } from "../../badge/Badge";
import { eligibilityTypes } from "../../../pages/deliveryCharges";
import { ReactNode, useState } from "react";
import {
  ContainerTypeEligibilityContainerTypesType,
  ContainerTypeEligibilityUnitTypesType,
} from "@sellar-io/api-v2/types";
import { useContainerTypeEligibilityRulesCreate } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesCreate";
import { useContainerTypeEligibilityRulesUpdate } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesUpdate";
import { useContainerTypeEligibilityRulesDelete } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesDelete";
import { twMerge } from "tailwind-merge";
import { InputLabel } from "@sellar-io/components";
import { SlideTransition } from "../../transition/SlideTransition";

export const ContainerEligibilityRulesPage = () => {
  const { setPage } = useFullPageDialogContext();
  const { business } = useSellarCurrentBusiness();

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: business?.Supplier?.id ?? 0,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!business?.Supplier?.id,
    },
  );

  const fulfillmentTypePages = fulfillmentTypeCourierQuery.isSuccess
    ? fulfillmentTypeCourierQuery.data.pages
    : [];

  const fulfillmentTypeList = fulfillmentTypePages.flat();

  const containerTypeEligibilityRulesList = fulfillmentTypeList.flatMap(
    (ft) => ft.ContainerTypeEligibilityRules,
  );

  const rulesByContainerType = Object.fromEntries(
    containerTypeEligibilityRulesList.map((rule) => [rule.containerType, rule]),
  );

  return (
    <>
      <DialogNavigation
        title="Define your courier container eligibility rules"
        description="Your customers will now be prompted to switch to a pallet service when ordering large containers. Below you can enter the largest container sizes your courier is able to accept."
      />
      <DialogContent className="flex flex-col gap-4">
        <Alert
          type="sellar"
          title="How do container eligibility rules work?"
          message={
            <>
              Your rules have been preset to default values based on what most
              couriers accept. You can edit them now or at any time in your
              courier delivery tier. For more details on the new update visit
              our{" "}
              <Anchor
                href="https://intercom.help/sellario/en/articles/12414132-supplier-guide-courier-eligibility-rules-pop-up"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600"
              >
                help centre
              </Anchor>{" "}
              and see how it works.
            </>
          }
          className="bg-zinc-50"
        />
        <div className="flex flex-col gap-4">
          <H5>Container rules</H5>
          {eligibilityTypes.map(({ display, containerType }) => {
            const rule = rulesByContainerType[containerType];

            return (
              <ContainerEligibilityCard
                key={containerType}
                display={display}
                text={
                  rule ? (
                    <>
                      {display} can be fulfilled by courier{" "}
                      <span className="font-semibold">
                        up to and including {rule.volume}
                        {rule.unit}
                      </span>
                    </>
                  ) : (
                    `${display} can only be fulfilled by pallet`
                  )
                }
                active={!!rule}
                id={rule?.id}
                containerType={containerType}
                unit={rule?.unit}
                volume={rule?.volume}
                FulfillmentTypeId={fulfillmentTypeList[0]?.id ?? 0}
              />
            );
          })}
        </div>
        <AlertCompact type="information" className="font-semibold">
          These changes will take effect 1st October, 2025
        </AlertCompact>
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={() =>
            setPage(CourierHaulierSwitchPromptPages.PALLET_DELIVERY_SETTINGS)
          }
        >
          Continue
        </Button>
      </DialogCTA>
    </>
  );
};

export const ContainerEligibilityCard = ({
  display,
  text,
  active,
  id,
  containerType,
  unit,
  volume,
  FulfillmentTypeId,
}: {
  display: string;
  text: ReactNode;
  active: boolean;
  id?: number;
  containerType: ContainerTypeEligibilityContainerTypesType;
  unit?: ContainerTypeEligibilityUnitTypesType;
  volume?: number;
  FulfillmentTypeId: number;
}) => {
  const [edit, setEdit] = useState(false);
  return (
    <>
      <Card
        className={twMerge(
          "bg-white",
          "flex cursor-pointer justify-between gap-4 border border-zinc-300 p-4 hover:bg-zinc-50",
          edit && "bg-zinc-100",
        )}
        onClick={() => setEdit(true)}
      >
        <div className="flex flex-col gap-1">
          <H5>{display}</H5>
          <span>{text}</span>
        </div>
        <div className="flex items-center gap-1">
          {active ? (
            <BadgeGreen>Active</BadgeGreen>
          ) : (
            <BadgeRed>Inactive</BadgeRed>
          )}
          <Icon icon="PENCIL" />
        </div>
      </Card>
      <SlideTransition show={edit}>
        <EditContainerEligibilityRule
          onDismiss={() => setEdit(false)}
          id={id}
          containerType={containerType}
          unit={unit}
          volume={volume}
          FulfillmentTypeId={FulfillmentTypeId}
        />
      </SlideTransition>
    </>
  );
};

const EditContainerEligibilityRule = ({
  onDismiss,
  id,
  containerType,
  unit,
  volume,
  FulfillmentTypeId,
}: {
  onDismiss: () => void;
  id?: number;
  containerType: ContainerTypeEligibilityContainerTypesType;
  unit?: ContainerTypeEligibilityUnitTypesType;
  volume?: number;
  FulfillmentTypeId: number;
}) => {
  const [updatedVolume, setUpdatedVolume] = useState<number | undefined>(
    volume,
  );
  const [updatedUnit, setUpdatedUnit] =
    useState<ContainerTypeEligibilityUnitTypesType>(unit ?? "L");
  const createContainerEligibilityRule =
    useContainerTypeEligibilityRulesCreate();
  const updateContainerEligibilityRule = useContainerTypeEligibilityRulesUpdate(
    id ?? 0,
  );
  const deleteContainerEligibilityRule = useContainerTypeEligibilityRulesDelete(
    id ?? 0,
  );

  const handleUpdate = () => {
    if (!updatedVolume || !updatedUnit) return;

    updateContainerEligibilityRule.mutate(
      {
        volume: updatedVolume,
        unit: updatedUnit,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  const handleCreate = () => {
    if (!updatedVolume || !updatedUnit) return;

    createContainerEligibilityRule.mutate(
      {
        containerType,
        volume: updatedVolume,
        unit: updatedUnit,
        FulfillmentTypeId,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  const onSubmit = () => {
    if (id) return handleUpdate();
    return handleCreate();
  };

  const onDelete = () => {
    deleteContainerEligibilityRule.mutate(undefined, {
      onSuccess: () => {
        setUpdatedVolume(undefined);
        setUpdatedUnit("L");
        onDismiss();
      },
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap gap-4 md:flex-nowrap">
        <div className="flex w-full flex-col gap-1">
          <InputLabel>*Maximum volume</InputLabel>
          <InputContainer className="flex items-center justify-between">
            <InputNumber
              value={updatedVolume}
              onChange={(e) => setUpdatedVolume(parseFloat(e.target.value))}
            />
          </InputContainer>
        </div>
        <div className="flex w-full flex-col gap-1">
          <InputLabel>*Unit</InputLabel>
          <InputContainer className="w-full">
            <InputSelect
              value={updatedUnit}
              onChange={(e) =>
                setUpdatedUnit(
                  e.target.value as ContainerTypeEligibilityUnitTypesType,
                )
              }
            >
              <InputOption value="L">Litres (L)</InputOption>
              <InputOption value="gal">Gallons (gal)</InputOption>
            </InputSelect>
          </InputContainer>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Icon icon="INFORMATION_OUTLINE" />
        <span>
          Remove this rule if you're unable to send containers of this type by
          courier.
        </span>
      </div>
      <div className="flex w-full items-center justify-end gap-2">
        <Button color="transparent" onClick={onDismiss}>
          Cancel
        </Button>
        {id && (
          <Button
            color="red"
            onClick={onDelete}
            loading={deleteContainerEligibilityRule.isPending}
          >
            Remove
          </Button>
        )}
        <Button
          disabled={!updatedUnit || !updatedVolume}
          onClick={onSubmit}
          loading={
            createContainerEligibilityRule.isPending ||
            updateContainerEligibilityRule.isPending
          }
        >
          Update
        </Button>
      </div>
    </div>
  );
};
